import {
    DatasetColumnDataType,
    DatasetColumnRole,
    DatasetType,
    SortType,
    TimeGranularity,
} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import type {Dataset} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import {AnalyticsQueryVariant, type DataResolverProps, type RawData} from "@vesta/vos-types/modules/workspaceLevel/domain/RawDataType";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";
import {dataResult} from "@vesta/error-handling-remix/result";
import type {QueryClient} from "@tanstack/react-query";
import type {AnalyticalEngineDataTransformationContext} from "~/queries/analyticalEngine/analyticalEngineQueries";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {fetchXochipilliDatasetData} from "~/workspaceConfig/hostedPages/dashboard/-api";
import {replaceNullStringDimensionsWithOthers} from "~/workspaceConfig/hostedPages/dashboard/-nullDimensionValues";
import {DashboardRowType, type DashboardConfig} from "~/workspaceConfig/hostedPages/dashboard/-types";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {WidgetType, type Widget} from "~/components/analyticsWidgets/widgetTypes/widget";

export const OTTOKART_QUOTATION_DASHBOARD_DATASET_ID = "dceee3f3-c09f-4cc6-b2bc-e3e3e5e77478" as Uuid;

const QUOTATION_METRIC_IDS = {
    quotations: "07229298-46eb-4beb-b025-a41523204425" as Uuid,
    quotationValue: "63c146eb-db04-4505-a3e2-87c263a50824" as Uuid,
    averageQuotationValue: "c92789dd-0198-443c-b0aa-2ea70dc450e0" as Uuid,
    quantity: "1d725fd0-dd26-4094-80e9-8fdef1d22b5b" as Uuid,
} as const;

const QUOTATION_STATE_DIMENSION_ID = "5d196505-fb00-4f74-a7af-a1f46ae12e9f" as Uuid;
const QUOTATION_PRODUCT_NAME_DIMENSION_ID = "6dc0161a-105c-4638-bff1-0c61d074de7a" as Uuid;
const QUOTATION_CATEGORY_DIMENSION_ID = "87c4184e-3ce1-4212-b518-0594477a2689" as Uuid;
const QUOTATION_STATUS_DIMENSION_ID = "54666084-cc62-45e7-aa3d-e1403ace06f2" as Uuid;
const QUOTATION_FILTER_DEFINITIONS = [
    {label: "Generated by", normalizedDisplayName: "generatedby"},
    {label: "Category", normalizedDisplayName: "category"},
    {label: "Brand", normalizedDisplayName: "brand"},
    {label: "Part Type", normalizedDisplayName: "parttype"},
    {label: "Machine", normalizedDisplayName: "machine"},
] as const;
const QUOTATION_CHART_COLOR_PALETTE = ["#4D92E4", "#78AEEB", "#2F6FB0", "#A9CCF2", "#1D5C99", "#C4DCF7"] as const;
const QUOTATION_DASHBOARD_GLOSSARIES = {
    quotations: "Number of quotations requested by customer and generated by the sales team.",
    quotationValue: "Total value of all quotations generated.",
    averageQuotationValue:
        "Average value of a quotation, calculated by dividing Total Quotation Value by Total Quotations.",
    stateSummary:
        "Shows the quotation count, total value, product quantity and average quotation value for each state.",
    category: "Shows the number of quotations for each product category.",
    productSummary:
        "Shows the top 20 products by quotation value, with quotation count, product quantity and average quotation value.",
    status: "Shows the number of quotations in each status.",
    quantityByStatus: "Shows the total product quantity included in quotations for each status.",
    quotationValueByStatus: "Shows the total value of quotations in each status.",
    quotationValueByCategory: "Shows the total value of quotations for each product category.",
    quantityByCategory: "Shows the total product quantity included in quotations for each product category.",
} as const;

export function getOttokartQuotationDashboardFilters(dataset: Dataset) {
    return QUOTATION_FILTER_DEFINITIONS.flatMap(({label, normalizedDisplayName}) => {
        const dimension = dataset.columns.find((column) => {
            const normalizedColumnDisplayName = column.displayName.toLowerCase().replace(/[^a-z]/g, "");
            return column.role === DatasetColumnRole.dimension && normalizedColumnDisplayName === normalizedDisplayName;
        });
        return dimension === undefined ? [] : [{id: dimension.id, label}];
    });
}

const QUOTATION_WIDGET_IDS = {
    quotations: "2e6f9d1a-6c5b-4a8e-9f2d-7b3c1d5e8a04" as Uuid,
    quotationValue: "4c8a1e7d-5b2f-46d9-8c3a-1e6f9b2d7a05" as Uuid,
    averageQuotationValue: "9b4e2c7a-1d6f-45a8-8c3b-5e0f2a7d4b07" as Uuid,
    quotationsTrend: "1f7a3c9e-5b2d-46e8-8c4a-0d6f2b9e3a08" as Uuid,
    quotationValueTrend: "3d9b5f1a-7c2e-48e6-a4b0-1f8c6d2e5a09" as Uuid,
} as const;

type QuotationDashboardConfigArguments = {
    getAdditionalColumns: CapabilityUdoColumnResolver;
    dataset: Dataset;
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
    queryClient: QueryClient;
    transformationContext: AnalyticalEngineDataTransformationContext;
    timeZone: Uuid;
    startTime: Iso8601DateTime;
    endTime: Iso8601DateTime;
};

export function getOttokartQuotationDashboardConfig({
    getAdditionalColumns,
    dataset,
    intellsysBaseUrl,
    accessToken,
    workspaceId,
    queryClient,
    transformationContext,
    timeZone,
    startTime,
    endTime,
}: QuotationDashboardConfigArguments): DashboardConfig {
    if (dataset.id !== OTTOKART_QUOTATION_DASHBOARD_DATASET_ID) {
        throw new Error(`Unexpected quotation dashboard dataset: ${dataset.id}`);
    }
    if (dataset.type !== DatasetType.periodic) {
        throw new Error("Quotation dashboard dataset must be periodic to render date-based bar graphs.");
    }

    const dateColumn = dataset.columns.find(
        (column) =>
            column.role === DatasetColumnRole.dimension &&
            (column.dataType === DatasetColumnDataType.date || column.dataType === DatasetColumnDataType.dateTime),
    );
    if (dateColumn === undefined) {
        throw new Error("Quotation dashboard dataset must contain a date or date-time dimension.");
    }

    const missingMetricIds = Object.values(QUOTATION_METRIC_IDS).filter(
        (metricId) => dataset.columns.some((column) => column.id === metricId) !== true,
    );
    if (missingMetricIds.length > 0) {
        throw new Error(`Quotation dashboard dataset is missing metrics: ${missingMetricIds.join(", ")}`);
    }

    const stateDimension = dataset.columns.find(
        (column) => column.id === QUOTATION_STATE_DIMENSION_ID && column.role === DatasetColumnRole.dimension,
    );
    if (stateDimension === undefined) {
        throw new Error("Quotation dashboard dataset is missing the State dimension required by the table.");
    }
    const productNameDimension = dataset.columns.find(
        (column) => column.id === QUOTATION_PRODUCT_NAME_DIMENSION_ID && column.role === DatasetColumnRole.dimension,
    );
    if (productNameDimension === undefined) {
        throw new Error("Quotation dashboard dataset is missing the Product Name dimension required by the table.");
    }
    const categoryDimension = dataset.columns.find(
        (column) => column.id === QUOTATION_CATEGORY_DIMENSION_ID && column.role === DatasetColumnRole.dimension,
    );
    if (categoryDimension === undefined) {
        throw new Error("Quotation dashboard dataset is missing the Category dimension required by the pie chart.");
    }
    const statusDimension = dataset.columns.find(
        (column) => column.id === QUOTATION_STATUS_DIMENSION_ID && column.role === DatasetColumnRole.dimension,
    );
    if (statusDimension === undefined) {
        throw new Error("Quotation dashboard dataset is missing the Status dimension required by the pie chart.");
    }

    const fetchData = async (_resolvedDataset: Dataset, props: DataResolverProps) => {
        const result = await fetchXochipilliDatasetData(
            getAdditionalColumns,
            intellsysBaseUrl,
            accessToken,
            workspaceId,
            queryClient,
            transformationContext,
            dataset,
            props,
        );
        if (result.success !== true) {
            return result;
        }
        return dataResult(replaceNullStringDimensionsWithOthers(result.data));
    };

    const makeNumberTile = (
        id: Uuid,
        title: string,
        metric: Uuid,
        decimalPlaces: number,
        valuePrefix: string = "",
        glossary?: string,
    ): Widget => ({
        id,
        type: WidgetType.numberTile,
        datasetId: dataset.id,
        datasetResolver: async () => dataResult(dataset),
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics: [metric],
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: (_resolvedDataset, props) => fetchData(_resolvedDataset, props),
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        numberSystem: NumberSystems.indian,
        decimalPlaces,
        spreadValue: true,
        valuePrefix,
        valueSuffix: "",
        ...(glossary === undefined ? {} : {glossary}),
    });

    const makeBarGraph = (id: Uuid, title: string, metric: Uuid, glossary?: string): Widget => ({
        id,
        type: WidgetType.multiBarGraph,
        datasetId: dataset.id,
        datasetResolver: async () => dataResult(dataset),
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [dateColumn.id],
        metrics: [metric],
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: (_resolvedDataset, props) => fetchData(_resolvedDataset, props),
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsPeriodic,
        chartColorPalette: QUOTATION_CHART_COLOR_PALETTE,
        granularityOptions: [TimeGranularity.daily, TimeGranularity.monthly, TimeGranularity.yearly],
        defaultGranularity: TimeGranularity.daily,
        ...(glossary === undefined ? {} : {glossary}),
    });

    const makeStateTable = (): Widget => ({
        id: uniqueUuid`5e1a7c3f-9b2d-46f8-a0c4-2d6e8b1f7a13`,
        type: WidgetType.table,
        datasetId: dataset.id,
        datasetResolver: async () => dataResult(dataset),
        title: "Quotation Summary by State",
        startDate: startTime,
        endDate: endTime,
        dimensions: [stateDimension.id],
        metrics: [
            QUOTATION_METRIC_IDS.quotations,
            QUOTATION_METRIC_IDS.quotationValue,
            QUOTATION_METRIC_IDS.quantity,
            QUOTATION_METRIC_IDS.averageQuotationValue,
        ],
        tableColumnHeaders: {
            [QUOTATION_METRIC_IDS.averageQuotationValue]: "Average Quotation Value (₹)",
            [QUOTATION_METRIC_IDS.quotationValue]: "Quotation Value (₹)",
        },
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: (_resolvedDataset, props) => fetchData(_resolvedDataset, props),
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        glossary: QUOTATION_DASHBOARD_GLOSSARIES.stateSummary,
    });

    const makeProductTable = (): Widget => ({
        id: uniqueUuid`8b2d6f1a-4c7e-49f0-a3d5-1e6c9a2b7f15`,
        type: WidgetType.table,
        datasetId: dataset.id,
        datasetResolver: async () => dataResult(dataset),
        title: "Top 20 Products by Quotation Value",
        startDate: startTime,
        endDate: endTime,
        dimensions: [productNameDimension.id],
        metrics: [
            QUOTATION_METRIC_IDS.quotations,
            QUOTATION_METRIC_IDS.quotationValue,
            QUOTATION_METRIC_IDS.quantity,
            QUOTATION_METRIC_IDS.averageQuotationValue,
        ],
        tableColumnHeaders: {
            [QUOTATION_METRIC_IDS.averageQuotationValue]: "Average Quotation Value (₹)",
            [QUOTATION_METRIC_IDS.quotationValue]: "Quotation Value (₹)",
        },
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: async (_resolvedDataset, props) => {
            const result = await fetchData(_resolvedDataset, {
                ...props,
                sort: [{id: QUOTATION_METRIC_IDS.quotationValue, sortType: SortType.descending}],
            });
            if (result.success !== true) {
                return result;
            }

            const quotationValueColumn = result.data.columnMetadata.find(
                (column) => column.id === QUOTATION_METRIC_IDS.quotationValue,
            );
            if (quotationValueColumn === undefined) {
                return result;
            }

            const getQuotationValue = (row: RawData["dataPoints"][number]): number => {
                const value = row[quotationValueColumn.columnIndex]?.value;
                const quotationValue = typeof value === "number" ? value : Number(value);
                return Number.isFinite(quotationValue) ? quotationValue : Number.NEGATIVE_INFINITY;
            };
            const dataPoints = [...result.data.dataPoints]
                .sort((left, right) => getQuotationValue(right) - getQuotationValue(left))
                .slice(0, 20);

            return dataResult({...result.data, dataPoints});
        },
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        glossary: QUOTATION_DASHBOARD_GLOSSARIES.productSummary,
    });

    const makeCategoryPieChart = (
        id: Uuid,
        title: string,
        metric: Uuid,
        glossary: string,
    ): Widget => ({
        id,
        type: WidgetType.pieChart,
        datasetId: dataset.id,
        datasetResolver: async () => dataResult(dataset),
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [categoryDimension.id],
        metrics: [metric],
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: (_resolvedDataset, props) => fetchData(_resolvedDataset, props),
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        chartColorPalette: QUOTATION_CHART_COLOR_PALETTE,
        glossary,
    });

    const makeStatusPieChart = (
        id: Uuid,
        title: string,
        metric: Uuid,
        glossary: string,
    ): Widget => ({
        id,
        type: WidgetType.pieChart,
        datasetId: dataset.id,
        datasetResolver: async () => dataResult(dataset),
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [statusDimension.id],
        metrics: [metric],
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: (_resolvedDataset, props) => fetchData(_resolvedDataset, props),
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        chartColorPalette: QUOTATION_CHART_COLOR_PALETTE,
        glossary,
    });

    return {
        id: uniqueUuid`6a2f8c1d-4e7b-49a5-b3d0-1f6c8e2a7b10`,
        title: "Quotation Dashboard",
        sections: [
            {
                id: uniqueUuid`8c1e5a7d-3f9b-46d2-a0e6-4b7c9d2f1a11`,
                title: "Key Metrics",
                rows: [
                    {
                        title: null,
                        type: DashboardRowType.numberTilesRow,
                        widgets: [
                            makeNumberTile(
                                QUOTATION_WIDGET_IDS.quotations,
                                "Total Quotations",
                                QUOTATION_METRIC_IDS.quotations,
                                0,
                                "",
                                QUOTATION_DASHBOARD_GLOSSARIES.quotations,
                            ),
                            makeNumberTile(
                                QUOTATION_WIDGET_IDS.quotationValue,
                                "Total Quotation value",
                                QUOTATION_METRIC_IDS.quotationValue,
                                2,
                                "₹",
                                QUOTATION_DASHBOARD_GLOSSARIES.quotationValue,
                            ),
                            makeNumberTile(
                                QUOTATION_WIDGET_IDS.averageQuotationValue,
                                "Average Quotation value",
                                QUOTATION_METRIC_IDS.averageQuotationValue,
                                2,
                                "₹",
                                QUOTATION_DASHBOARD_GLOSSARIES.averageQuotationValue,
                            ),
                        ],
                    },
                ],
            },
            {
                id: uniqueUuid`1d7b3f9a-5c2e-48f6-a0b4-6e8d2c1f9a12`,
                title: "Trends",
                rows: [
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeBarGraph(
                                QUOTATION_WIDGET_IDS.quotationsTrend,
                                "Total Quotations",
                                QUOTATION_METRIC_IDS.quotations,
                                QUOTATION_DASHBOARD_GLOSSARIES.quotations,
                            ),
                            makeBarGraph(
                                QUOTATION_WIDGET_IDS.quotationValueTrend,
                                "Total Quotation value",
                                QUOTATION_METRIC_IDS.quotationValue,
                                QUOTATION_DASHBOARD_GLOSSARIES.quotationValue,
                            ),
                        ],
                    },
                ],
            },
            {
                id: uniqueUuid`9c2a4e7f-1d6b-48a3-b5c0-7e2f9a1d6b18`,
                title: "Category Analytics",
                rows: [
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeCategoryPieChart(
                                uniqueUuid`d8f16ef1-5af3-4df8-bc3d-c0feeb36a4c2`,
                                "Quotation Value by Category",
                                QUOTATION_METRIC_IDS.quotationValue,
                                QUOTATION_DASHBOARD_GLOSSARIES.quotationValueByCategory,
                            ),
                            makeCategoryPieChart(
                                uniqueUuid`8a43b021-e63f-4824-9ad2-0bd79a2495ce`,
                                "Quantity by Category",
                                QUOTATION_METRIC_IDS.quantity,
                                QUOTATION_DASHBOARD_GLOSSARIES.quantityByCategory,
                            ),
                        ],
                    },
                ],
            },
            {
                id: uniqueUuid`7f3b1d9e-5a2c-46e8-b0d4-1c6f9a2e7b14`,
                title: "Quotation Details",
                rows: [
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeStatusPieChart(
                                uniqueUuid`e6e1cc90-8a93-4489-a563-3a470e1f2d2e`,
                                "Quotation Value by Status",
                                QUOTATION_METRIC_IDS.quotationValue,
                                QUOTATION_DASHBOARD_GLOSSARIES.quotationValueByStatus,
                            ),
                            makeStatusPieChart(
                                uniqueUuid`bb5b2d72-09b0-4d8e-88de-5bc29869bb02`,
                                "Quantity by Status",
                                QUOTATION_METRIC_IDS.quantity,
                                QUOTATION_DASHBOARD_GLOSSARIES.quantityByStatus,
                            ),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.tableRow,
                        widgets: [makeStateTable()],
                    },
                    {
                        title: null,
                        type: DashboardRowType.tableRow,
                        widgets: [makeProductTable()],
                    },
                ],
            },
        ],
    };
}
